import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("🔥 CRITICAL FAILURE:", error, errorInfo);
    // Firestore "INTERNAL ASSERTION FAILED: Unexpected state" poisons the SDK's
    // async queue — every later call throws until the page reloads. Session
    // state (path/stats/login) lives in localStorage, so one reload is nearly
    // invisible; showing a reviewer a SYSTEM FAILURE screen is not. Reload
    // once per minute at most, so a truly stuck client still surfaces.
    const msg = String(error && (error.message || error));
    if (/INTERNAL ASSERTION FAILED/i.test(msg)) {
      try {
        const last = Number(sessionStorage.getItem('bq_assert_reload') || 0);
        if (Date.now() - last > 60000) {
          sessionStorage.setItem('bq_assert_reload', String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* fall through to the screen */ }
    }
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        // FIXED: Added z-[9999], fixed positioning, and high contrast background
        <div className="fixed inset-0 z-[9999] h-screen w-screen bg-slate-950 text-red-500 p-6 flex flex-col items-center justify-center overflow-hidden font-mono">
          <div className="bg-slate-900 border-2 border-red-500/50 rounded-2xl p-6 max-w-md w-full shadow-2xl relative z-50">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-500" size={32} />
              <h1 className="text-xl font-black text-white uppercase tracking-tighter">System Failure</h1>
            </div>

            <div className="bg-black/50 rounded-xl p-4 mb-6 overflow-x-auto border border-white/5">
              <p className="text-xs text-red-400 font-bold mb-2">ERROR REPORT:</p>
              <code className="text-[10px] text-slate-300 block mb-2">
                {this.state.error && this.state.error.toString()}
              </code>
            </div>

            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-black py-4 rounded-xl flex items-center justify-center gap-2 uppercase tracking-widest transition-all"
            >
              <RefreshCw size={18} /> Reboot System
            </button>
          </div>
        </div>
      );
    }

    return this.props.children; 
  }
}

export default ErrorBoundary;
